import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, UtensilsCrossed, Phone, ShoppingCart } from 'lucide-react';
import { CONTACT_INFO, ROUTE_PATHS } from '@/lib/index';
import { useCart } from '@/hooks/useCart';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CartDrawer } from '@/components/CartDrawer';

interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Layout component providing consistent navigation and footer across the restaurant site.
 * Focused on reliability and simple user flow as per business requirements.
 */
export function Layout({ children }: LayoutProps) {
  const { toggleCart, getTotalItems } = useCart();
  
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Navigation Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border bg-background/80 backdrop-blur-md">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link 
            to={ROUTE_PATHS.HOME} 
            className="flex items-center gap-2 group"
          >
            <div className="bg-primary p-1.5 rounded-md text-primary-foreground group-hover:scale-110 transition-transform duration-200">
              <UtensilsCrossed size={20} />
            </div>
            <span className="font-bold text-lg tracking-tight uppercase">
              {CONTACT_INFO.name}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to={ROUTE_PATHS.HOME} 
              className="text-sm font-medium hover:text-primary transition-colors"
            >
              Menu Sets
            </Link>
            <a 
              href="#delivery" 
              className="text-sm font-medium hover:text-primary transition-colors"
            >
              Delivery Areas
            </a>
            <a 
              href="#addons" 
              className="text-sm font-medium hover:text-primary transition-colors"
            >
              Add-ons
            </a>
          </nav>

          <div className="flex items-center gap-3">
            <a 
              href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} 
              className="hidden sm:flex items-center gap-2 text-sm font-mono text-muted-foreground hover:text-foreground transition-colors"
            >
              <Phone size={14} />
              {CONTACT_INFO.phone}
            </a>
            
            {/* Cart Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={toggleCart}
              className="relative rounded-full"
            >
              <ShoppingCart size={18} />
              {getTotalItems() > 0 && (
                <Badge className="absolute -top-2 -right-2 w-5 h-5 p-0 flex items-center justify-center bg-primary text-primary-foreground text-xs">
                  {getTotalItems()}
                </Badge>
              )}
            </Button>
            
            <Button 
              asChild 
              size="sm" 
              className="rounded-full bg-primary text-primary-foreground hover:opacity-90"
            >
              <a 
                href={CONTACT_INFO.whatsappUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <MessageCircle size={18} />
                <span>Order Now</span>
              </a>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer Section */}
      <footer className="bg-muted py-12 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
            <div className="space-y-4">
              <h3 className="font-bold text-xl">{CONTACT_INFO.name}</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Authentic Nasi Arab Mandy based in {CONTACT_INFO.baseLocation}. 
                Freshly prepared and delivered right to your doorstep.
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-sm uppercase tracking-wider">Delivery Partners</h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                {CONTACT_INFO.deliveryServices.map((service) => (
                  <li key={service}>{service}</li>
                ))}
              </ul>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-sm uppercase tracking-wider">Contact Us</h4>
              <div className="space-y-2 font-mono text-sm">
                <p>{CONTACT_INFO.phone}</p>
                <p>Meru, Klang, Selangor</p>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-border/50 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
            <p>© {CONTACT_INFO.year} {CONTACT_INFO.name}. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Cart Drawer */}
      <CartDrawer />
    </div>
  );
}
